import { Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import Adsense from '../Ads/Adsense';
import { ExpertCallout } from '../shared/ExpertCallout';
import { InflationChart } from './Chart/InflationChart';
import { InflationForm } from './Form/InflationForm';
import {
  calculateInflation,
  getInflationCallout,
  InflationFormValues,
  InflationResult,
} from './InflationPage.hooks';
import styles from './InflationPage.module.css';

function InflationPage() {
  const navigate = useNavigate();
  const resultRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<InflationResult>();
  const [values, setValues] = useState<InflationFormValues>();

  const handleSubmit = useCallback((nextValues: InflationFormValues) => {
    logEvent('calculator', 'inflation_calculate', 'submit');
    setValues(nextValues);
    setResult(calculateInflation(nextValues));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(
    () => (result && values ? getInflationCallout(result, values.inflationRate) : null),
    [result, values]
  );

  return (
    <div className={styles.root}>
      <Helmet>
        <title>물가 상승 계산기 — 내 노후 자금의 실질 가치 | 노후든든</title>
        <meta
          name="description"
          content="인플레이션을 반영한 노후 자금 계산기. 현재 월 생활비가 물가 상승 후 얼마가 필요한지, 실질 구매력을 유지하려면 얼마를 더 모아야 하는지 확인하세요."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>물가 상승 계산기</h1>
          <p className={styles.pageDesc}>
            지금 생활비가 은퇴 시점에 얼마가 되는지, 그리고 물가를 반영하면 목표 자산이 얼마나 달라지는지 확인하세요.
          </p>
        </div>
        <div className={styles.formCard}>
          <InflationForm onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && values && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              <div className={styles.resultHeroCard}>
                <p className={styles.resultHeroLabel}>은퇴 시점 실질 월 생활비</p>
                <p className={styles.resultHeroAmount}>{formatManwon(result.realMonthlyExpenseAtRetirement)}</p>
                <p className={styles.resultHeroSub}>
                  연 {values.inflationRate}% 물가 상승 {values.yearsToRetirement}년 후 기준
                </p>
              </div>

              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>물가 반영 총 필요 자금</p>
                  <p className={styles.metricValue}>{formatManwon(result.realTotalNeeded)}</p>
                  <p className={styles.metricInterpretation}>실질 구매력 기준 은퇴 자금</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>추가로 필요한 자금</p>
                  <p className={styles.metricValue}>{formatManwon(result.inflationGap)}</p>
                  <p className={styles.metricInterpretation}>명목 계획 대비 추가 필요액</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>100만원의 미래 가치</p>
                  <p className={styles.metricValue}>{formatManwon(result.purchasingPowerOf1M)}</p>
                  <p className={styles.metricInterpretation}>{values.yearsToRetirement}년 후 구매력</p>
                </div>
              </div>

              <div className={styles.chartCard}>
                <p className={styles.chartTitle}>월 생활비 변화 — 명목 vs 물가 반영</p>
                <div className={styles.chartWrapper}>
                  <InflationChart yearlyData={result.yearlyData} />
                </div>
              </div>

              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    logEvent('engagement', 'inflation_cta_click', '/retirement/goal');
                    navigate(`/retirement/goal?goal=${result.realTotalNeeded}`);
                  }}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  이 목표 자산으로 월 저축 계획 세우기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="INFLATION_AD_SLOT" />

              <p className={styles.disclaimer}>
                이 계산기는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
                세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default InflationPage;
